import { Injectable, inject } from '@angular/core';
import { deleteField, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';

import { User } from '../models/user.model';
import { DataStoreService } from './data-store.service';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly firebase = inject(FirebaseService);
  private readonly dataStore = inject(DataStoreService);

  async getCurrentUserDocument(forceFetch = false): Promise<User> {
    const inMemoryUser = this.dataStore.getProperty('currentUser');
    if (!forceFetch && inMemoryUser && inMemoryUser.userId) {
      return inMemoryUser;
    }

    await this.firebase.auth.authStateReady();
    const authUser = this.firebase.auth.currentUser;
    if (!authUser) {
      throw new Error('User not found');
    }

    const userDoc = await getDoc(doc(this.firebase.globalFirestore, `users/${authUser.uid}`));
    const currentUser = (userDoc.data() as User) ?? ({} as User);
    if (!currentUser.userId) {
      currentUser.userId = authUser.uid;
    }
    this.dataStore.updateState({ currentUser });

    return currentUser;
  }

  async updateDisplayName(displayName: string): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');
    await updateProfile(user, { displayName });
  }

  async uploadProfilePicture(file: File): Promise<string> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');

    const currentUser = await this.getCurrentUserDocument();
    const oldStoragePath = currentUser.profilePictureStoragePath;

    const ext = file.name.split('.').pop() ?? 'png';
    const storagePath = `users/${user.uid}/profile-picture/${Date.now()}.${ext}`;
    const storageRef = ref(this.firebase.globalStorage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file, { contentType: file.type });

    await new Promise<void>((resolve, reject) => {
      uploadTask.on('state_changed', null, reject, () => resolve());
    });

    const downloadUrl = await getDownloadURL(storageRef);
    await setDoc(doc(this.firebase.globalFirestore, `users/${user.uid}`), {
      profilePictureUrl: downloadUrl,
      profilePictureStoragePath: storagePath,
    }, { merge: true });

    if (oldStoragePath && oldStoragePath !== storagePath) {
      await deleteObject(ref(this.firebase.globalStorage, oldStoragePath)).catch(() => { /* best-effort cleanup */ });
    }

    await updateProfile(user, { photoURL: downloadUrl }).catch(() => { /* non-fatal */ });

    currentUser.profilePictureUrl = downloadUrl;
    currentUser.profilePictureStoragePath = storagePath;
    this.dataStore.updateState({ currentUser: { ...currentUser } });
    return downloadUrl;
  }

  async removeProfilePicture(): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');

    const currentUser = await this.getCurrentUserDocument();
    const storagePath = currentUser.profilePictureStoragePath;

    await updateDoc(doc(this.firebase.globalFirestore, `users/${user.uid}`), {
      profilePictureUrl: deleteField(),
      profilePictureStoragePath: deleteField(),
    });

    if (storagePath) {
      await deleteObject(ref(this.firebase.globalStorage, storagePath)).catch(() => { /* best-effort cleanup */ });
    }

    await updateProfile(user, { photoURL: null }).catch(() => { /* non-fatal */ });

    delete currentUser.profilePictureUrl;
    delete currentUser.profilePictureStoragePath;
    this.dataStore.updateState({ currentUser: { ...currentUser } });
  }
}
